import { logEvent } from '../lib/analytics'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { isOverlayBusy } from './Celebration'
import { railBusy, subscribeRail } from '../lib/toastRail'
import { capture } from '../lib/batchReceipt'

// ── StreakMilestone ───────────────────────────────────────────────────────────
// A full-screen flame-mint moment for 'nextep:streak-milestone', built as a
// direct sibling of AchievementUnlock.jsx (same medallion-drop/shine-sweep
// choreography, flame-flavored instead of tiered bronze/silver/gold) — this
// closes the cinematic-gap the fable-mandate audit flagged: streaks were the
// single most emotionally loaded RECURRING moment in the app (loss aversion,
// daily habit) but got the same plain toast as routine saves.
//
// Only fires at real round numbers (see the callers' isStreakMilestone checks
// in useWatchlist.jsx / Cliffhanger.jsx) — every other day of a streak keeps
// the existing lightweight nextep:milestone toast. Firing this every single
// day from day 2 onward would be exactly the annoying-high-frequency-moment
// the "keep frequent actions fast" principle warns against; this is the rare
// big-number payoff, not the daily receipt.
//
// Same no-double-surface rule as achievement-unlocked: callers dispatch EITHER
// this OR the toast for a given streak day, never both.
export function StreakMilestoneListener() {
  const navigate = useNavigate()
  const [queue, setQueue] = useState([])
  const [stage, setStage] = useState(0)
  const [, setGate] = useState(0)
  const timers = useRef([])
  const idRef = useRef(0)

  useEffect(() => {
    // Same rail-wait + late-enqueue as AchievementUnlock (see its header): the mark
    // that earned the milestone also fired an Undo bar, and that bar goes first.
    const pending = []
    const onMilestone = (e) => {
      const m = e.detail
      if (!m?.title) return
      if (capture('streak', e)) return   // a cascade's receipt lists it instead
      pending.push(setTimeout(() => setQueue(q => [...q, { ...m, _id: ++idRef.current }]), 300))
    }
    window.addEventListener('nextep:streak-milestone', onMilestone)
    return () => { window.removeEventListener('nextep:streak-milestone', onMilestone); pending.forEach(clearTimeout) }
  }, [])

  const item = queue[0] || null
  const presentingId = useRef(null)
  const held = !!item && presentingId.current !== item._id && (isOverlayBusy() || railBusy('streak'))

  useEffect(() => {
    if (!held) return
    const iv = setInterval(() => setGate(g => g + 1), 450)
    return () => clearInterval(iv)
  }, [held])
  useEffect(() => subscribeRail(() => setGate(g => g + 1)), [])

  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  useEffect(() => {
    timers.current.forEach(clearTimeout)
    timers.current = []
    if (!item || held) { setStage(0); return }
    presentingId.current = item._id
    if (typeof window !== 'undefined') window.__nextepFinaleActive = true
    logEvent('cinematic_shown', { kind: 'streak', title: item.title })
    if (reduce) { setStage(3); return }
    setStage(0)
    timers.current.push(
      setTimeout(() => setStage(1), 60),
      setTimeout(() => setStage(2), 260),
      setTimeout(() => setStage(3), 660),
    )
    return () => timers.current.forEach(clearTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?._id, held])

  const dismiss = () => {
    if (!item) return
    if (typeof window !== 'undefined') window.__nextepFinaleActive = false
    setQueue(q => q.slice(1))
  }

  useEffect(() => {
    if (!item || held || stage < 3) return
    const t = setTimeout(dismiss, 3400)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?._id, held, stage])

  if (!item || held) return null

  const color = '#f5822a'   // flame orange — distinct from the amber/gold tiers so a
  const rgb   = '245,130,42' // streak medallion never reads as "just another achievement"

  return createPortal((
    <div
      onClick={dismiss}
      style={{
        position: 'fixed', inset: 0, zIndex: 'var(--z-celebration)',
        background: `radial-gradient(circle at 50% 38%, rgba(${rgb},0.18) 0%, rgba(var(--bg-rgb),0.94) 65%), rgba(var(--bg-rgb),0.82)`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font)', cursor: 'pointer',
      }}
    className={reduce ? undefined : 'sm-fade-in'}
    >
      <div style={{ position: 'relative', width: 180, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
        {stage >= 1 && !reduce && (
          <>
            <div style={{ position: 'absolute', inset: 0, borderRadius: 'var(--radius-full)', border: `2px solid ${color}` }} className="sm-ring" />
            <div style={{ position: 'absolute', inset: 0, borderRadius: 'var(--radius-full)', border: `2px solid ${color}` }} className="sm-ring sm-ring-2" />
          </>
        )}
        <div style={{
          position: 'absolute', inset: -10, borderRadius: 'var(--radius-full)',
          background: `radial-gradient(circle, rgba(${rgb},0.5), transparent 70%)`, filter: 'blur(14px)',
        }} className={reduce ? undefined : 'sm-glow'} />
        <div style={{
          position: 'relative', width: 128, height: 128, borderRadius: 'var(--radius-full)',
          background: `linear-gradient(155deg, ${color}, rgba(0,0,0,0.35))`,
          border: `3px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 10px 40px rgba(0,0,0,0.6), 0 0 40px rgba(${rgb},0.5)`, overflow: 'hidden',
        }} className={reduce ? undefined : (stage >= 1 ? 'sm-shake' : 'sm-drop')}>
          <div style={{ position: 'absolute', inset: 6, borderRadius: 'var(--radius-full)', border: '1.5px dashed #fff', opacity: 0.5 }} />
          <span style={{ fontSize: 52, position: 'relative', zIndex: 2 }}>{item.icon || '🔥'}</span>
          {stage >= 2 && !reduce && (
            <div style={{
              position: 'absolute', top: '-30%', bottom: '-30%', left: 0, width: '35%',
              background: 'linear-gradient(105deg, transparent, rgba(var(--ink-rgb),0.55), transparent)',
              pointerEvents: 'none',
            }} className="sm-shine" />
          )}
        </div>
      </div>

      {stage >= 3 && (
        <div style={{ textAlign: 'center' }} className={reduce ? undefined : 'sm-text'}>
          <div style={{ color, fontSize: 11, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 8 }}>Streak Milestone</div>
          <div style={{ fontFamily: 'var(--font-serif)', color: '#fff', fontSize: 28, fontWeight: 600, marginBottom: 8 }}>{item.title}</div>
          {item.body && <div style={{ color: 'rgba(var(--text-rgb),0.55)', fontSize: 14, maxWidth: 280, margin: '0 auto 22px' }}>{item.body}</div>}
          <button onClick={(e) => { e.stopPropagation(); dismiss(); navigate(item.route || '/profile?tab=stats') }} style={{
            padding: '12px 22px', background: color, border: 'none', borderRadius: 'var(--radius)',
            color: 'var(--amber-ink)', fontWeight: 800, fontSize: 13.5, cursor: 'pointer', fontFamily: 'var(--font)',
          }}>Keep it going →</button>
          <div style={{ color: 'rgba(var(--ink-rgb),0.35)', fontSize: 10.5, marginTop: 16 }}>tap anywhere to continue</div>
        </div>
      )}

      <style>{`
        @keyframes smFadeIn { from{opacity:0} to{opacity:1} }
        @keyframes smMedallionDrop { 0%{opacity:0;transform:translateY(-40px) scale(0.7)} 60%{opacity:1;transform:translateY(6px) scale(1.05)} 80%{transform:translateY(-3px) scale(0.98)} 100%{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes smImpactShake { 0%{transform:scale(1)} 20%{transform:scale(0.94)} 45%{transform:scale(1.03)} 70%{transform:scale(0.99)} 100%{transform:scale(1)} }
        @keyframes smRingOut { 0%{opacity:0.8;transform:scale(0.3)} 100%{opacity:0;transform:scale(2.6)} }
        @keyframes smGlowPulse { 0%,100%{opacity:0.5} 50%{opacity:0.95} }
        @keyframes smTextUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .sm-fade-in { animation: smFadeIn 0.4s ease; }
        .sm-ring { animation: smRingOut 0.9s ease-out forwards; }
        .sm-ring-2 { animation-delay: 0.15s; }
        .sm-glow { animation: smGlowPulse 2.2s ease-in-out infinite; }
        .sm-drop { animation: smMedallionDrop 0.55s cubic-bezier(0.34,1.2,0.64,1) both; }
        .sm-shake { animation: smImpactShake 0.5s ease; }
        .sm-shine { animation: smShineSweep 0.9s ease-out forwards; }
        .sm-text { animation: smTextUp 0.5s ease both; }
        @keyframes smShineSweep { 0%{transform:translateX(-160%) rotate(20deg)} 100%{transform:translateX(220%) rotate(20deg)} }
      `}</style>
    </div>
  ), document.body)
}
